import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, X, Truck, PackageOpen, PackageCheck, Clock, ArrowUpRight, ArrowDownLeft, ExternalLink } from 'lucide-react';
import apiService from '../../services/ApiService';

// ── Apple Design System ────────────────────────────────────────────────────────
const FONT     = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif';
const BG       = '#F2F2F7';
const INK      = '#1C1C1E';
const MUTED    = '#8E8E93';
const SECONDARY = '#3C3C43';
const SYS_GREEN  = '#34C759';
const SYS_RED    = '#FF3B30';
const SYS_BLUE   = '#007AFF';
const SYS_ORANGE = '#FF9500';
const BRAND      = '#84CC16';

const CARD_STYLE = {
  background: '#fff',
  borderRadius: 18,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 20px rgba(0,0,0,0.07)',
};

const INP = {
  padding: '11px 14px',
  border: 'none',
  borderRadius: 10,
  fontSize: 15,
  outline: 'none',
  background: BG,
  color: INK,
  fontFamily: FONT,
  width: '100%',
  boxSizing: 'border-box',
};

const LBL = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: MUTED,
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
  marginBottom: 8,
};
// ──────────────────────────────────────────────────────────────────────────────

const CARRIERS   = ['UPS', 'FedEx', 'USPS', 'DHL', 'Freight', 'Other'];
const STATUSES   = ['pending', 'in_transit', 'delivered', 'cancelled'];
const STATUS_LABEL = { pending: 'Pending', in_transit: 'In Transit', delivered: 'Delivered', cancelled: 'Cancelled' };
const STATUS_COLOR = {
  pending:    { color: SYS_ORANGE, bg: '#FFF8ED' },
  in_transit: { color: SYS_BLUE,   bg: '#EFF6FF' },
  delivered:  { color: SYS_GREEN,  bg: '#F0FDF4' },
  cancelled:  { color: MUTED,      bg: BG        },
};

function ShipmentModal({ shipment, onSave, onClose }) {
  const isEdit = !!shipment;
  const [form, setForm] = useState({
    type: 'outbound', status: 'pending', carrier: 'UPS',
    trackingNumber: '', contact: '', address: '',
    items: '', weight: '', shipDate: '', estimatedDelivery: '',
    actualDelivery: '', notes: '',
    ...(shipment || {}),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.contact.trim()) { setError('Recipient / Supplier name is required.'); return; }
    setSaving(true); setError('');
    try { await onSave(form); onClose(); }
    catch (err) { setError(err.message || 'Failed to save.'); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 560, boxShadow: '0 24px 80px rgba(0,0,0,0.18)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', fontFamily: FONT }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', flexShrink: 0 }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: INK, letterSpacing: '-0.02em', margin: 0 }}>{isEdit ? 'Edit Shipment' : 'New Shipment'}</h2>
            <p style={{ fontSize: 13, color: MUTED, marginTop: 3, marginBottom: 0 }}>{isEdit ? 'Update shipment details' : 'Track an outgoing or incoming shipment'}</p>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: '50%', background: BG, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, flexShrink: 0 }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '4px 24px 24px' }}>
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{ background: '#FFF2F2', borderRadius: 10, padding: '11px 14px', color: SYS_RED, fontSize: 13, marginBottom: 16, fontFamily: FONT }}>
                {error}
              </div>
            )}

            {/* Type + Status */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
              <div>
                <label style={LBL}>Direction</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['outbound', 'inbound'].map(t => (
                    <button key={t} type="button" onClick={() => set('type', t)} style={{
                      flex: 1, padding: '9px 0', borderRadius: 10, border: 'none',
                      background: form.type === t ? BRAND : BG,
                      color: form.type === t ? '#fff' : SECONDARY,
                      fontWeight: form.type === t ? 700 : 400, fontSize: 13, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                      fontFamily: FONT, transition: 'all 0.15s',
                    }}>
                      {t === 'outbound' ? <ArrowUpRight size={13} /> : <ArrowDownLeft size={13} />}
                      {t === 'outbound' ? 'Outbound' : 'Inbound'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="sh-status" style={LBL}>Status</label>
                <select id="sh-status" value={form.status} onChange={e => set('status', e.target.value)} style={{ ...INP, cursor: 'pointer' }}>
                  {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </div>
            </div>

            {/* Contact */}
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="sh-contact" style={LBL}>{form.type === 'outbound' ? 'Customer / Recipient *' : 'Supplier / Sender *'}</label>
              <input id="sh-contact" type="text" value={form.contact} onChange={e => set('contact', e.target.value)} required style={INP} placeholder={form.type === 'outbound' ? 'Customer name or company' : 'Supplier or vendor name'} />
            </div>

            {/* Carrier + Tracking */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
              <div>
                <label htmlFor="sh-carrier" style={LBL}>Carrier</label>
                <select id="sh-carrier" value={form.carrier} onChange={e => set('carrier', e.target.value)} style={{ ...INP, cursor: 'pointer' }}>
                  {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="sh-tracking" style={LBL}>Tracking Number</label>
                <input id="sh-tracking" type="text" value={form.trackingNumber} onChange={e => set('trackingNumber', e.target.value)} style={INP} placeholder="Optional" />
              </div>
            </div>

            {/* Address */}
            {form.type === 'outbound' && (
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="sh-address" style={LBL}>Delivery Address</label>
                <input id="sh-address" type="text" value={form.address} onChange={e => set('address', e.target.value)} style={INP} placeholder="Street, City, State, ZIP" />
              </div>
            )}

            {/* Items + Weight */}
            <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 14, marginBottom: 16 }}>
              <div>
                <label htmlFor="sh-items" style={LBL}>Items / Description</label>
                <input id="sh-items" type="text" value={form.items} onChange={e => set('items', e.target.value)} style={INP} placeholder="e.g. Torsion springs x10, cables x4" />
              </div>
              <div>
                <label htmlFor="sh-weight" style={LBL}>Weight</label>
                <input id="sh-weight" type="text" value={form.weight} onChange={e => set('weight', e.target.value)} style={INP} placeholder="e.g. 12 lbs" />
              </div>
            </div>

            {/* Dates */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
              <div>
                <label htmlFor="sh-shipdate" style={LBL}>Ship Date</label>
                <input id="sh-shipdate" type="date" value={form.shipDate} onChange={e => set('shipDate', e.target.value)} style={INP} />
              </div>
              <div>
                <label htmlFor="sh-est" style={LBL}>Est. Delivery</label>
                <input id="sh-est" type="date" value={form.estimatedDelivery} onChange={e => set('estimatedDelivery', e.target.value)} style={INP} />
              </div>
            </div>

            {form.status === 'delivered' && (
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="sh-actual" style={LBL}>Actual Delivery Date</label>
                <input id="sh-actual" type="date" value={form.actualDelivery} onChange={e => set('actualDelivery', e.target.value)} style={INP} />
              </div>
            )}

            {/* Notes */}
            <div style={{ marginBottom: 24 }}>
              <label htmlFor="sh-notes" style={LBL}>Notes</label>
              <textarea id="sh-notes" value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...INP, resize: 'vertical' }} placeholder="Any additional notes..." />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} style={{ padding: '11px 22px', border: 'none', borderRadius: 10, background: BG, cursor: 'pointer', fontSize: 15, color: SECONDARY, fontFamily: FONT, fontWeight: 500 }}>
                Cancel
              </button>
              <button type="submit" disabled={saving} style={{ padding: '11px 26px', border: 'none', borderRadius: 10, background: BRAND, color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 15, fontWeight: 600, opacity: saving ? 0.75 : 1, fontFamily: FONT }}>
                {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Shipment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const { color, bg } = STATUS_COLOR[status] || STATUS_COLOR.pending;
  return (
    <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, color, background: bg, fontFamily: FONT, letterSpacing: '0.01em' }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function TypeBadge({ type }) {
  return type === 'outbound'
    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 8, fontSize: 11, fontWeight: 600, color: '#7c3aed', background: '#F5F3FF', fontFamily: FONT }}><ArrowUpRight size={10} />Out</span>
    : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 8, fontSize: 11, fontWeight: 600, color: SYS_BLUE, background: '#EFF6FF', fontFamily: FONT }}><ArrowDownLeft size={10} />In</span>;
}

function trackingUrl(carrier, number) {
  if (!number) return null;
  const urls = {
    UPS:   `https://www.ups.com/track?tracknum=${number}`,
    FedEx: `https://www.fedex.com/apps/fedextrack/?tracknumbers=${number}`,
    USPS:  `https://tools.usps.com/go/TrackConfirmAction?tLabels=${number}`,
    DHL:   `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${number}`,
  };
  return urls[carrier] || null;
}

export default function ShippingPage() {
  const [shipments, setShipments]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(null); // null | 'new' | shipment object
  const [search, setSearch]         = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try { setShipments(await apiService.getShipments()); }
    catch { setShipments([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave(form) {
    if (modal === 'new') {
      const created = await apiService.createShipment(form);
      setShipments(prev => [created, ...prev]);
    } else {
      const updated = await apiService.updateShipment(form.id, form);
      setShipments(prev => prev.map(s => s.id === updated.id ? updated : s));
    }
  }

  async function handleDelete(s) {
    if (!window.confirm(`Delete shipment for "${s.contact}"?`)) return;
    await apiService.deleteShipment(s.id);
    setShipments(prev => prev.filter(x => x.id !== s.id));
  }

  async function quickStatus(s, status) {
    const updated = await apiService.updateShipment(s.id, { ...s, status });
    setShipments(prev => prev.map(x => x.id === updated.id ? updated : x));
  }

  // Stats
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

  const pending        = shipments.filter(s => s.status === 'pending').length;
  const inTransit      = shipments.filter(s => s.status === 'in_transit').length;
  const deliveredMonth = shipments.filter(s => s.status === 'delivered' && s.actualDelivery >= monthStart).length;
  const outbound       = shipments.filter(s => s.type === 'outbound').length;

  // Filter
  const filtered = shipments.filter(s => {
    if (filterType !== 'all' && s.type !== filterType) return false;
    if (filterStatus !== 'all' && s.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      return (s.contact || '').toLowerCase().includes(q) ||
             (s.trackingNumber || '').toLowerCase().includes(q) ||
             (s.items || '').toLowerCase().includes(q) ||
             (s.carrier || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Pill helper for filter toggles
  const PILL = (label, active, onClick) => (
    <button onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 20, border: 'none',
      background: active ? BRAND : BG,
      color: active ? '#fff' : SECONDARY,
      fontSize: 13, fontWeight: active ? 600 : 400, cursor: 'pointer',
      fontFamily: FONT, transition: 'all 0.15s',
    }}>{label}</button>
  );

  // Inline stat card component
  const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div style={{ ...CARD_STYLE, padding: '20px 22px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ ...LBL, marginBottom: 0 }}>{title}</span>
        <div style={{ width: 32, height: 32, borderRadius: 9, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} color={color} />
        </div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: INK, lineHeight: 1, fontFamily: FONT }}>{value}</div>
      <div style={{ fontSize: 11, color: MUTED, marginTop: 5, fontFamily: FONT }}>{subtitle}</div>
    </div>
  );

  return (
    <div className="main-scroll" style={{ height: '100%', overflowY: 'auto', padding: '24px 28px', background: BG, fontFamily: FONT }}>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginBottom: 22 }}>
        <StatCard title="Pending"        value={pending}        subtitle="awaiting shipment"  icon={Clock}        color={SYS_ORANGE} />
        <StatCard title="In Transit"     value={inTransit}      subtitle="currently moving"   icon={Truck}        color={SYS_BLUE}   />
        <StatCard title="Delivered"      value={deliveredMonth} subtitle="this month"         icon={PackageCheck} color={SYS_GREEN}  />
        <StatCard title="Total Outbound" value={outbound}       subtitle="outgoing shipments" icon={PackageOpen}  color={BRAND}      />
      </div>

      {/* Toolbar */}
      <div style={{ ...CARD_STYLE, padding: '14px 18px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>

          {/* Search */}
          <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 200 }}>
            <Search size={14} color={MUTED} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input
              type="text" placeholder="Search contact, tracking, items…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ padding: '10px 14px 10px 34px', border: 'none', borderRadius: 10, fontSize: 14, outline: 'none', background: BG, color: INK, fontFamily: FONT, width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          {/* Direction filters */}
          <div style={{ display: 'flex', gap: 6, background: BG, borderRadius: 12, padding: 4 }}>
            {PILL('All', filterType === 'all', () => setFilterType('all'))}
            {PILL('Outbound', filterType === 'outbound', () => setFilterType('outbound'))}
            {PILL('Inbound', filterType === 'inbound', () => setFilterType('inbound'))}
          </div>

          {/* Status filters */}
          <div style={{ display: 'flex', gap: 6, background: BG, borderRadius: 12, padding: 4 }}>
            {PILL('All Status', filterStatus === 'all', () => setFilterStatus('all'))}
            {PILL('Pending', filterStatus === 'pending', () => setFilterStatus('pending'))}
            {PILL('In Transit', filterStatus === 'in_transit', () => setFilterStatus('in_transit'))}
            {PILL('Delivered', filterStatus === 'delivered', () => setFilterStatus('delivered'))}
          </div>

          <button
            onClick={() => setModal('new')}
            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: BRAND, border: 'none', borderRadius: 12, color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14, flexShrink: 0, fontFamily: FONT }}
          >
            <Plus size={15} /> New Shipment
          </button>
        </div>
      </div>

      {/* Shipment List */}
      <div style={{ ...CARD_STYLE, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: MUTED, fontSize: 14, fontFamily: FONT }}>Loading shipments…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 56, textAlign: 'center', fontFamily: FONT }}>
            <Truck size={38} color="#E5E5EA" style={{ marginBottom: 14 }} />
            <p style={{ color: MUTED, fontSize: 15, fontWeight: 500, margin: '0 0 6px' }}>No shipments found</p>
            <p style={{ color: '#C7C7CC', fontSize: 13, margin: 0 }}>Add your first shipment to get started</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: FONT }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #F2F2F7' }}>
                {['Type', 'Contact', 'Carrier / Tracking', 'Items', 'Ship Date', 'Est. Delivery', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '12px 16px', color: MUTED, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', fontFamily: FONT }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => {
                const url = trackingUrl(s.carrier, s.trackingNumber);
                const isLast = i === filtered.length - 1;
                return (
                  <tr
                    key={s.id}
                    style={{ borderBottom: isLast ? 'none' : '1px solid #F9F9F9' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '14px 16px' }}><TypeBadge type={s.type} /></td>

                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 600, color: INK, fontSize: 14 }}>{s.contact}</div>
                      {s.address && <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{s.address}</div>}
                    </td>

                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 500, color: SECONDARY, fontSize: 13 }}>{s.carrier}</div>
                      {s.trackingNumber ? (
                        url ? (
                          <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: SYS_BLUE, display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 2, textDecoration: 'none' }}>
                            {s.trackingNumber} <ExternalLink size={9} />
                          </a>
                        ) : (
                          <div style={{ fontSize: 11, color: MUTED, marginTop: 2, fontFamily: 'monospace' }}>{s.trackingNumber}</div>
                        )
                      ) : (
                        <div style={{ fontSize: 11, color: '#C7C7CC', marginTop: 2 }}>No tracking</div>
                      )}
                    </td>

                    <td style={{ padding: '14px 16px', color: SECONDARY, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.items || <span style={{ color: '#C7C7CC' }}>—</span>}
                    </td>

                    <td style={{ padding: '14px 16px', color: MUTED, whiteSpace: 'nowrap', fontSize: 13 }}>{s.shipDate || '—'}</td>

                    <td style={{ padding: '14px 16px', color: MUTED, whiteSpace: 'nowrap', fontSize: 13 }}>
                      {s.status === 'delivered' && s.actualDelivery ? s.actualDelivery : (s.estimatedDelivery || '—')}
                    </td>

                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                        <StatusBadge status={s.status} />
                        {s.status === 'pending' && (
                          <button onClick={() => quickStatus(s, 'in_transit')} title="Mark In Transit" style={{ padding: '3px 9px', borderRadius: 8, border: 'none', background: '#EFF6FF', color: SYS_BLUE, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>
                            → Transit
                          </button>
                        )}
                        {s.status === 'in_transit' && (
                          <button onClick={() => quickStatus(s, 'delivered')} title="Mark Delivered" style={{ padding: '3px 9px', borderRadius: 8, border: 'none', background: '#F0FDF4', color: SYS_GREEN, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>
                            → Delivered
                          </button>
                        )}
                      </div>
                    </td>

                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => setModal(s)}
                          style={{ padding: '5px 12px', border: 'none', borderRadius: 8, background: BG, cursor: 'pointer', color: SECONDARY, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, fontFamily: FONT, fontWeight: 500 }}
                        >
                          <Edit2 size={11} /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(s)}
                          style={{ padding: '5px 10px', border: 'none', borderRadius: 8, background: '#FFF2F2', cursor: 'pointer', color: SYS_RED, fontSize: 12, display: 'flex', alignItems: 'center', fontFamily: FONT }}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <ShipmentModal
          shipment={modal === 'new' ? null : modal}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
